/**
 * TextToolService.ts;
 * Service pour l'outil texte avanc\u00e9;
 *
 * Fonctionnalit\u00e9s :
 * - Mode \u00e9dition texte;
 * - Variables dynamiques {{nom}}
 * - Auto-resize (width, height, both);
 * - Effets avanc\u00e9s (ombre, contour, fond);
 * - Application de styles nomm\u00e9s;
 */
import Konva from 'konva';
import { logger } from '../utils/ProfessionalLogger';
import { TextStyleManager } from '../managers/TextStyleManager';
import { useAppStore } from './StateService';
// InlineTextEditor supprimé - fonctionnalité intégrée dans CanvasArea.tsx
import type { CanvasManager } from '../types/LegacyCanvasManagerTypes';
import type { TextAutoResize } from '../types';
import { EventBus } from './EventBus';
import { sanitizeHTML } from '../utils/DOMSanitizer';
export interface TextToolConfig {
  canvasManager: CanvasManager;
  textStyleManager: TextStyleManager;
}
export interface TextVariables {
  [key: string]: string;
}
/**
 * Service pour l'outil texte avanc\u00e9;
 */
export class TextToolService {
  private config: TextToolConfig;
  private isActive: boolean = false;
  private currentTextNode: Konva.Text | null = null;
  private variables: TextVariables = {};
  // InlineTextEditor supprimé - l'édition est gérée par CanvasArea.tsx
  constructor(config: TextToolConfig) {
    this.config = config;
  }
  /**
   * Initialise le service;
   */
  public initialize(): void {
    // Charger les variables sauvegard\u00e9es;
    this.loadVariables();
    logger.info('\u2705 TextToolService initialis\u00e9');
  }
  /**
   * Active l'outil texte;
   */
  public activate(): void {
    this.isActive = true;
    const store = useAppStore.getState();
    store.setActiveTool('text');
    logger.info('\u270d\ufe0f Outil texte activ\u00e9');
  }
  /**
   * D\u00e9sactive l'outil texte;
   */
  public deactivate(): void {
    this.isActive = false;
    this.currentTextNode = null;
    const store = useAppStore.getState();
    store.setActiveTool(null);
    logger.info('\u270d\ufe0f Outil texte d\u00e9sactiv\u00e9');
  }
  /**
   * Cr\u00e9e un nouveau texte sur le canvas;
   * CORRIGÉ: Synchronise maintenant avec le store Zustand pour l'affichage dans CanvasArea.tsx
   */
  public createText(
    text: string = 'Double-cliquez pour \u00e9diter',
    x: number = 100,
    y: number = 100,
    styleId?: string
  ): Konva.Text {
    const textNode = new Konva.Text({
      x,
      y,
      text,
      fontSize: 16,
      fontFamily: 'Arial',
      fill: '#000000',
      draggable: true,
      width: 200,
    });
    // Appliquer un style si sp\u00e9cifi\u00e9;
    if (styleId) {
      this.config.textStyleManager.applyStyleToNode(textNode, styleId);
    }
    // Ajouter au canvas;
    this.config.canvasManager.getLayer().add(textNode);
    this.config.canvasManager.optimizeViewportRendering();

    // CORRIGÉ: Synchroniser avec le store Zustand pour que le texte s'affiche dans CanvasArea.tsx
    this.syncTextToStore(textNode);

    // CORRIGÉ: Mettre à jour le compteur de calques dans le Store
    this.config.canvasManager.updateLayerCountInStore();

    // Double-clic pour \u00e9diter;
    this.attachEditHandlers(textNode);
    logger.info('\u2705 Texte cr\u00e9\u00e9 et synchronis\u00e9 avec le store');
    return textNode;
  }

  /**
   * Synchronise un TextNode avec le store Zustand
   * CORRIGÉ: Nécessaire pour que les textes s'affichent dans CanvasArea.tsx
   */
  private syncTextToStore(textNode: Konva.Text): void {
    try {
      const store = useAppStore.getState();
      const shapeData: import('../types/ShapeTypes').ShapeData = {
        id: textNode.id(),
        type: 'text',
        x: textNode.x(),
        y: textNode.y(),
        rotation: textNode.rotation(),
        opacity: textNode.opacity(),
        visible: textNode.visible(),
        locked: textNode.getAttr('locked') || false,
        text: textNode.text(),
        fontSize: textNode.fontSize(),
        fontFamily: textNode.fontFamily(),
        fontStyle: textNode.fontStyle(),
        textAlign: textNode.align() as 'left' | 'center' | 'right' | 'justify',
        textDecoration: textNode.textDecoration(),
        fill: typeof textNode.fill() === 'string' ? (textNode.fill() as string) : '#000000',
        width: textNode.width(),
        height: textNode.height(),
        shadowBlur: textNode.shadowBlur(),
        shadowColor: textNode.shadowColor(),
        shadowOffsetX: textNode.shadowOffsetX(),
        shadowOffsetY: textNode.shadowOffsetY(),
      };

      // Vérifier si la shape existe déjà dans le Store
      const existingShape = store.shapes.find(s => s.id === shapeData.id);

      if (existingShape) {
        // Mettre à jour la shape existante
        store.updateShape(shapeData.id, shapeData);
      } else {
        // Ajouter la nouvelle shape
        store.addShape(shapeData);
      }
    } catch (error) {
      logger.error('Erreur lors de la synchronisation du texte avec le Store:', error);
    }
  }
  /**
   * Attache les handlers d'\u00e9dition \u00e0 un TextNode;
   * NOTE: Les handlers de double-clic sont maintenant gérés par CanvasArea.tsx
   * Cette méthode est maintenue pour compatibilité mais ne fait rien
   */
  private attachEditHandlers(_textNode: Konva.Text): void {
    // Les handlers de double-clic sont gérés par CanvasArea.tsx dans handleTextDoubleClick
    // Pas besoin d'attacher des handlers ici car CanvasArea.tsx gère déjà le double-clic
    logger.debug('attachEditHandlers appelé - les handlers sont gérés par CanvasArea.tsx');
  }
  /**
   * Attache les handlers d'\u00e9dition \u00e0 un TextNode existant;
   * Méthode publique pour permettre l'attachement de handlers à des textes créés ailleurs;
   */
  public attachEditHandlersToExistingText(textNode: Konva.Text): void {
    this.attachEditHandlers(textNode);
    logger.info("✅ Handlers d'édition attachés au texte existant");
  }
  /**
   * D\u00e9marre l'\u00e9dition d'un texte;
   * NOTE: L'édition est maintenant gérée par CanvasArea.tsx via le composant TextEditor
   * Cette méthode est maintenue pour compatibilité mais ne fait rien
   * L'édition se déclenche automatiquement via le double-clic géré dans CanvasArea.tsx
   */
  public startEditing(textNode: Konva.Text): void {
    this.currentTextNode = textNode;
    const shapeId = textNode.id();

    // PHASE 2.1: Émettre un événement pour déclencher l'édition dans CanvasArea.tsx
    const eventBus = EventBus.getInstance();
    eventBus.emit({
      type: 'text-edit-start',
      timestamp: Date.now(),
      data: { shapeId, textNode },
    });

    logger.debug(`📝 Édition de texte déclenchée pour shapeId: ${shapeId}`);
  }
  /**
   * G\u00e8re la fin d'\u00e9dition du texte;
   * CORRIGÉ: Synchronise avec le store après modification
   */
  private handleTextFinish(textNode: Konva.Text, newText: string): void {
    textNode.text(newText);
    textNode.show();

    // CORRIGÉ: Synchroniser avec le store après modification
    this.syncTextToStore(textNode);

    this.config.canvasManager.optimizeViewportRendering();
    // Appliquer l'auto-resize si configur\u00e9;
    const autoResize = textNode.getAttr('autoResize') as TextAutoResize;
    if (autoResize) {
      this.applyAutoResize(textNode, autoResize);
      // Re-synchroniser après auto-resize
      this.syncTextToStore(textNode);
    }
    this.config.canvasManager.optimizeViewportRendering();
    this.currentTextNode = null;
  }
  /**
   * G\u00e8re l'annulation d'\u00e9dition du texte;
   */
  private handleTextCancel(): void {
    if (this.currentTextNode) {
      this.currentTextNode.show();
      this.config.canvasManager.optimizeViewportRendering();
    }
    this.currentTextNode = null;
  }
  /**
   * Applique un style nomm\u00e9 \u00e0 un TextNode;
   */
  public applyStyle(textNode: Konva.Text, styleId: string): void {
    this.config.textStyleManager.applyStyleToNode(textNode, styleId);
    this.config.canvasManager.optimizeViewportRendering();
  }
  /**
   * Applique l'auto-resize \u00e0 un TextNode;
   */
  public applyAutoResize(textNode: Konva.Text, mode: TextAutoResize): void {
    textNode.setAttr('autoResize', mode);
    switch (mode) {
      case 'width':
        // Konva.Text accepte 'auto' pour width mais TypeScript ne le reconnaît pas
        textNode.width('auto' as unknown as number);
        break;
      case 'height':
        textNode.height('auto' as unknown as number);
        break;
      case 'both':
        textNode.width('auto' as unknown as number);
        textNode.height('auto' as unknown as number);
        break;
      case 'none':
      default:
        // Pas d'auto-resize;
        break;
    }
    this.config.canvasManager.optimizeViewportRendering();
  }
  /**
   * D\u00e9finit une variable;
   */
  public setVariable(key: string, value: string): void {
    this.variables[key] = value;
    this.saveVariables();
    // Mettre \u00e0 jour tous les textes qui utilisent cette variable;
    this.updateAllTextsWithVariables();
    logger.info(`\ud83d\udcdd Variable d\u00e9finie: {{${key}}} = "${value}"`);
  }
  /**
   * R\u00e9cup\u00e8re une variable;
   */
  public getVariable(key: string): string | undefined {
    return this.variables[key];
  }
  /**
   * R\u00e9cup\u00e8re toutes les variables;
   */
  public getAllVariables(): TextVariables {
    return { ...this.variables };
  }
  /**
   * Supprime une variable;
   */
  public deleteVariable(key: string): void {
    delete this.variables[key];
    this.saveVariables();
    this.updateAllTextsWithVariables();
    logger.info(`\ud83d\uddd1\ufe0f Variable supprim\u00e9e: {{${key}}}`);
  }
  /**
   * Traite les variables dans un texte;
   * CORRIGÉ: Sanitize le texte pour prévenir les attaques XSS
   */
  public processVariables(text: string): string {
    if (typeof text !== 'string') {
      return '';
    }
    // Sanitize d'abord pour prévenir XSS
    let processed = sanitizeHTML(text);
    // Remplacer toutes les variables {{key}}
    Object.keys(this.variables).forEach(key => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      const variableValue = this.variables[key] || '';
      // Sanitize aussi les valeurs des variables
      const sanitizedValue = sanitizeHTML(variableValue);
      processed = processed.replace(regex, sanitizedValue);
    });
    return processed;
  }
  /**
   * Met \u00e0 jour tous les textes avec les variables actuelles;
   */
  private updateAllTextsWithVariables(): void {
    const textNodes = this.config.canvasManager.getLayer().find('Text');
    textNodes.forEach((node: Konva.Node) => {
      if (node instanceof Konva.Text) {
        const originalText = node.getAttr('originalText');
        if (originalText) {
          const processedText = this.processVariables(originalText);
          node.text(processedText);
        }
      }
    });
    this.config.canvasManager.optimizeViewportRendering();
  }
  /**
   * Ajoute une ombre \u00e0 un TextNode;
   */
  public addShadow(
    textNode: Konva.Text,
    config: {
      color: string;
      blur: number;
      offsetX: number;
      offsetY: number;
      opacity: number;
    }
  ): void {
    textNode.shadowColor(config.color);
    textNode.shadowBlur(config.blur);
    textNode.shadowOffsetX(config.offsetX);
    textNode.shadowOffsetY(config.offsetY);
    textNode.shadowOpacity(config.opacity);
    textNode.shadowEnabled(true);
    this.config.canvasManager.optimizeViewportRendering();
  }
  /**
   * Ajoute un contour \u00e0 un TextNode;
   */
  public addStroke(
    textNode: Konva.Text,
    config: {
      color: string;
      width: number;
    }
  ): void {
    textNode.stroke(config.color);
    textNode.strokeWidth(config.width);
    this.config.canvasManager.optimizeViewportRendering();
  }
  /**
   * Ajoute un fond \u00e0 un TextNode;
   */
  public addBackground(
    textNode: Konva.Text,
    config: {
      color: string;
      padding: number;
      cornerRadius: number;
    }
  ): void {
    // Cr\u00e9er un rectangle en arri\u00e8re-plan;
    const rect = new Konva.Rect({
      x: textNode.x() - config.padding,
      y: textNode.y() - config.padding,
      width: textNode.width() + config.padding * 2,
      height: textNode.height() + config.padding * 2,
      fill: config.color,
      cornerRadius: config.cornerRadius,
    });
    // Ins\u00e9rer le rectangle avant le texte;
    const layer = this.config.canvasManager.getLayer();
    rect.moveToBottom();
    layer.add(rect);
    // Associer le rectangle au texte;
    textNode.setAttr('backgroundRect', rect);
    layer.batchDraw();
  }
  /**
   * Sauvegarde les variables dans localStorage;
   */
  private saveVariables(): void {
    try {
      localStorage.setItem('snapbooth_text_variables', JSON.stringify(this.variables));
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde des variables:', error);
    }
  }
  /**
   * Charge les variables depuis localStorage;
   */
  private loadVariables(): void {
    try {
      const saved = localStorage.getItem('snapbooth_text_variables');
      if (saved) {
        this.variables = JSON.parse(saved);
        logger.info(`\ud83d\udcdd ${Object.keys(this.variables).length} variables charg\u00e9es`);
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des variables:', error);
    }
  }
  /**
   * Exporte les variables en JSON;
   */
  public exportVariables(): string {
    return JSON.stringify(this.variables, null, 2);
  }
  /**
   * Importe des variables depuis JSON;
   */
  public importVariables(jsonString: string): void {
    try {
      const imported: TextVariables = JSON.parse(jsonString);
      this.variables = { ...this.variables, ...imported };
      this.saveVariables();
      this.updateAllTextsWithVariables();
      logger.info(`\u2705 ${Object.keys(imported).length} variables import\u00e9es`);
    } catch (error) {
      logger.error("Erreur lors de l'import des variables:", error);
    }
  }
  /**
   * V\u00e9rifie si l'outil est actif;
   */
  public isToolActive(): boolean {
    return this.isActive;
  }
  /**
   * Nettoie les ressources;
   */
  public destroy(): void {
    this.deactivate();
    // L'édition est gérée par CanvasArea.tsx
    this.currentTextNode = null;
    logger.info('\ud83e\uddf9 TextToolService nettoy\u00e9');
  }
}
